export type RiskIndicator =
  | "stageProgression"
  | "closeDateInPast"
  | "closeDateSlippage"
  | "dependsOnFewLargeDeals"
  | "lastModifiedDate"
  | "stageRegression"
  | "noNextStepAssigned"
  | "nextActivityUnscheduled"
  | "lastActivityTooLongAgo"
  | "stalled"
  | "toneOfVoice"
  | "emailResponseTime"
  | "stakeholderInvolvement"
  | "probabilityInflation"
  | "emailRatio"
  | "regression"
  | "weightedValueOverBudget"
  | "salesTarget"
  | "pipelineFilled"
  | "numberOfMeetings"
  | "leadSource"
  | "netRevenue"
  | "totalTimeToClose";

export type RiskIndicatorGroup =
  | "dealProgression"
  | "portfolioRisk"
  | "buyerMomentum"
  | "salesExecution";

export type RiskCategory =
  | "pipelineRisk"
  | "forecast"
  | "costOfGrowth";

export type RiskIndicatorGrouping = Record<RiskIndicatorGroup, RiskIndicator[]>;
export type RiskIndicatorsInCategory = Record<RiskCategory, RiskIndicator[]>;

export function formatRiskPoints(value: number | null | undefined) {
  const score = value === null || value === undefined ? Number.NaN : Number(value);
  return Number.isFinite(score) ? `${Math.round(score)} pts` : "—";
}

export function formatRiskPointChange(
  previous: number | null | undefined,
  current: number | null | undefined,
) {
  const previousScore = previous === null || previous === undefined
    ? Number.NaN
    : Number(previous);
  const currentScore = current === null || current === undefined
    ? Number.NaN
    : Number(current);

  if (Number.isFinite(previousScore) && Number.isFinite(currentScore)) {
    return `${Math.round(previousScore)} → ${Math.round(currentScore)} pts`;
  }
  if (Number.isFinite(currentScore)) return formatRiskPoints(currentScore);
  if (Number.isFinite(previousScore)) return formatRiskPoints(previousScore);
  return "—";
}

export const riskIndicatorGroupings: RiskIndicatorGrouping = {
  salesExecution: [
    "lastActivityTooLongAgo",
    "lastModifiedDate",
    "emailRatio",
  ],
  buyerMomentum: [
    "toneOfVoice",
    "emailResponseTime",
    "stakeholderInvolvement",
  ],
  dealProgression: [
    "closeDateInPast",
    "closeDateSlippage",
    "stageProgression",
    "stageRegression",
  ],
  portfolioRisk: [
    "dependsOnFewLargeDeals",
  ],
};

type RiskIndicatorCopy = {
  title: string;
  description?: string;
  lostReason?: string;
};

export const riskIndicatorCopy: Partial<Record<RiskIndicator, RiskIndicatorCopy>> = {
  stageProgression: {
    title: "Deal momentum",
    description: "Slower deal progression is a strong early indicator that commercial momentum has weakened and the deal is becoming less likely to close.",
    lostReason: "Lost deal momentum",
  },
  closeDateInPast: {
    title: "Close plan",
    description: "Deals that remain open beyond their planned close date are significantly more likely to have lost buyer alignment and commercial momentum.",
    lostReason: "Lost buyer alignment",
  },
  closeDateSlippage: {
    title: "Close commitment",
    description: "Repeated close date changes are a strong indicator that buyer commitment or decision timing has weakened.",
    lostReason: "Weak close commitment",
  },
  dependsOnFewLargeDeals: {
    title: "Pipeline concentration",
  },
  lastModifiedDate: {
    title: "Seller engagement",
    description: "Delayed CRM updates are a strong early indicator that seller ownership or deal momentum is weakening, significantly reducing the likelihood of a successful outcome.",
    lostReason: "Seller disengagement",
  },
  stageRegression: {
    title: "Buying process",
    description: "Moving backwards in the buying process is a strong indicator that buyer alignment has been lost and the deal requires requalification.",
    lostReason: "Buying process stalled",
  },
  noNextStepAssigned: {
    title: "Deal ownership",
    description: "Deals without a confirmed next step are significantly more likely to lose momentum, indicating weak deal ownership and execution.",
    lostReason: "Weak deal ownership",
  },
  nextActivityUnscheduled: {
    title: "Unscheduled next activity",
    description: "Deals identified as not having a next activity assigned.",
  },
  lastActivityTooLongAgo: {
    title: "Buyer engagement",
    description: "Extended gaps in buyer communication are a strong early indicator that buyer engagement is weakening, increasing the likelihood of a lost deal.",
    lostReason: "Buyer disengagement",
  },
  stalled: {
    title: "Deal has stalled",
  },
  toneOfVoice: {
    title: "Buyer confidence",
    description: "Changes in buyer language are a strong early indicator of hesitation, uncertainty, or declining confidence in the purchase decision.",
    lostReason: "Low buyer confidence",
  },
  emailResponseTime: {
    title: "Buyer responsiveness",
    description: "Slower buyer response times are a strong early indicator of declining urgency and slower internal decision-making.",
    lostReason: "Low buyer responsiveness",
  },
  stakeholderInvolvement: {
    title: "Stakeholder coverage",
    description: "Limited stakeholder coverage is a strong indicator that buying consensus has not been established, increasing the likelihood of a lost deal.",
    lostReason: "Insufficient stakeholder coverage",
  },
  probabilityInflation: {
    title: "Forecast confidence",
    description: "Historical forecasting patterns show that similar deals close significantly less often than the seller's forecast suggests.",
    lostReason: "Overestimated forecast",
  },
  emailRatio: {
    title: "Engagement balance",
    description: "One-sided communication is a strong indicator that the seller is driving the deal without matching buyer commitment.",
    lostReason: "Seller-driven deal",
  },
  regression: { title: "Regression" },
  weightedValueOverBudget: { title: "Weighted value over budget" },
  salesTarget: { title: "Sales target" },
  pipelineFilled: { title: "Pipeline filled" },
  numberOfMeetings: { title: "Number of meetings" },
  leadSource: { title: "Lead source" },
  netRevenue: { title: "Net revenue" },
  totalTimeToClose: { title: "Total time to close" },
};

export function mapRiskName(riskKey: RiskIndicator) {
  return riskIndicatorCopy[riskKey]?.title ?? riskKey;
}

export function getRiskIndicatorDescription(riskKey: RiskIndicator) {
  return riskIndicatorCopy[riskKey]?.description ?? null;
}

export function mapRiskLostReasonName(riskKey: RiskIndicator) {
  return riskIndicatorCopy[riskKey]?.lostReason ?? mapRiskName(riskKey);
}

export function mapRiskGroupName(riskGroup: RiskIndicatorGroup) {
  switch (riskGroup) {
    case "dealProgression": return "Deal progression";
    case "portfolioRisk": return "Portfolio risk";
    case "buyerMomentum": return "Buyer momentum";
    case "salesExecution": return "Sales execution";
    default: return "Other risks";
  }
}

export function getRiskGroupDescription(riskGroup: RiskIndicatorGroup) {
  switch (riskGroup) {
    case "dealProgression":
      return "Monitor the physics of the deal. Identify if your timeline is realistic or if the deal is starting to stall.";
    case "portfolioRisk":
      return "Assess the deal’s share of overall pipeline value. Determine if it represents a high concentration risk.";
    case "buyerMomentum":
      return "Measure the buyer’s level of interest and commitment. Analyze behavioral signals to see if the prospect is leaning in or pulling away.";
    case "salesExecution":
      return "Track how actively your team is managing each deal. Use these metrics to ensure consistent sales processes and maintain administrative hygiene.";
    default:
      return "Track other risks affecting the deal";
  }
}

export function mapRiskCategoryName(riskCategory: RiskCategory) {
  switch (riskCategory) {
    case "pipelineRisk": return "Pipeline Risk";
    case "forecast": return "Forecast";
    case "costOfGrowth": return "Cost of Growth";
    default: return "";
  }
}

export const categoryIndicators: RiskIndicatorsInCategory = {
  pipelineRisk: [
    "stageProgression",
    "closeDateInPast",
    "closeDateSlippage",
    "lastModifiedDate",
    "stageRegression",
    "emailResponseTime",
    "lastActivityTooLongAgo",
    "toneOfVoice",
    "emailRatio",
    "stakeholderInvolvement",
    "dependsOnFewLargeDeals",
  ],
  forecast: [
    "stageProgression",
    "closeDateInPast",
    "closeDateSlippage",
    "lastModifiedDate",
    "stageRegression",
    "emailResponseTime",
    "lastActivityTooLongAgo",
    "toneOfVoice",
    "emailRatio",
    "stakeholderInvolvement",
  ],
  costOfGrowth: [
    "numberOfMeetings",
  ],
};
